<div class="set-maintenance-page container-fluid">
  <!-- Global Loading State -->
  <div *ngIf="isLoading" class="page-loading-overlay">
    <div class="loading-card">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Loading system configuration...</p>
    </div>
  </div>

  <!-- Main Content -->
  <div *ngIf="!isLoading">
    <!-- Maintenance Mode Card -->
    <mat-card appearance="outlined" class="col-md-12 col-lg-9">
      <mat-card-content>
        <div class="mat-h3">Maintenance mode</div>
        <ui-lib-status-toggle
          [showCancelConfirmation]="false"
          [formControl]="formControlMaintenanceMode">
        </ui-lib-status-toggle>
      </mat-card-content>
    </mat-card>

    <!-- Feature Flags Card -->
    <mat-card appearance="outlined" class="col-md-12 col-lg-9 feature-flags-card">
      <mat-card-content>
        <div class="card-header-simple">
          <h2 class="card-title">Feature Flags</h2>
          <p class="card-subtitle">Manage system feature toggles</p>
        </div>

        <div *ngIf="featureFlags.length === 0" class="empty-state">
          <span class="mdi mdi-flag-outline empty-icon"></span>
          <p>No feature flags configured</p>
        </div>

        <div *ngIf="featureFlags.length > 0" class="feature-flags-list">
          <div *ngFor="let flag of featureFlags; let last = last" class="feature-flag-item">
            <div class="flag-content">
              <div class="flag-main">
                <div class="flag-title-row">
                  <h3 class="flag-name">{{ getFlagLabel(flag.featureName) }}</h3>
                  <span class="system-badge" [class.system-active]="flag.isActive" [class.system-inactive]="!flag.isActive">
                    <span class="mdi" [class.mdi-check-circle]="flag.isActive" [class.mdi-close-circle]="!flag.isActive"></span>
                    {{ flag.isActive ? 'System Active' : 'System Inactive' }}
                  </span>
                </div>
                
                <p class="flag-description" *ngIf="flag.description">{{ flag.description }}</p>
                
                <div class="flag-details">
                  <div class="detail-item current-value">
                    <span class="detail-icon mdi mdi-cog"></span>
                    <span class="detail-label">Setting:</span>
                    <span class="detail-value" [class.value-enabled]="flag.flagValue" [class.value-disabled]="!flag.flagValue">
                      {{ getFlagValueLabel(flag) }}
                    </span>
                  </div>
                  
                  <div class="detail-item" *ngIf="flag.updatedByName">
                    <span class="detail-icon mdi mdi-account"></span>
                    <span class="detail-label">Modified by:</span>
                    <span class="detail-value">{{ flag.updatedByName }}</span>
                  </div>
                  
                  <div class="detail-item" *ngIf="flag.lastUpdatedTime">
                    <span class="detail-icon mdi mdi-clock-outline"></span>
                    <span class="detail-label">Last update:</span>
                    <span class="detail-value">{{ formatDate(flag.lastUpdatedTime) }}</span>
                  </div>
                </div>
              </div>

              <div class="flag-actions">
                <div class="toggle-control">
                  <mat-slide-toggle
                    [(ngModel)]="flag.flagValue"
                    (change)="onFeatureFlagToggleChange(flag)"
                    [disabled]="!flag.isActive"
                    color="primary">
                  </mat-slide-toggle>
                </div>
              </div>
            </div>

            <!-- User Access Management Section -->
            <div class="user-access-section" *ngIf="flag.isActive && flag.flagValue">
              <div class="user-access-header">
                <button mat-button (click)="toggleUserAccessExpansion(flag)" class="user-access-toggle">
                  <span class="mdi" [class.mdi-chevron-down]="isUserAccessExpanded(flag)" [class.mdi-chevron-right]="!isUserAccessExpanded(flag)"></span>
                  <span>User Access Control</span>
                  <span class="user-count-badge" *ngIf="hasUserAccessRestrictions(flag)">
                    {{ (selectedUsersForFlag[flag.featureName] || flag.allowedUserIds || []).length }} user(s)
                  </span>
                </button>
              </div>

              <div class="user-access-content" *ngIf="isUserAccessExpanded(flag)">
                <p class="user-access-description">
                  <span *ngIf="!hasUserAccessRestrictions(flag)">
                    No user restrictions. This feature is available to all users when enabled.
                  </span>
                  <span *ngIf="hasUserAccessRestrictions(flag)">
                    This feature is restricted to specific users. Only selected users will have access.
                  </span>
                </p>

                <!-- Selected Users -->
                <div class="selected-users" *ngIf="hasUserAccessRestrictions(flag)">
                  <div class="selected-users-header">
                    <span class="section-label">Allowed Users:</span>
                  </div>
                  <div class="user-chips">
                    <mat-chip-set>
                      @for (userId of (selectedUsersForFlag[flag.featureName] || flag.allowedUserIds || []); track userId) {
                        <mat-chip>
                          <span>{{ getUserName(userId) }}</span>
                          <span class="user-email" *ngIf="getUserEmail(userId)"> ({{ getUserEmail(userId) }})</span>
                          <button matChipRemove (click)="removeUserFromFlag(flag, userId)">
                            <mat-icon>cancel</mat-icon>
                          </button>
                        </mat-chip>
                      }
                    </mat-chip-set>
                  </div>
                </div>

                <!-- Add User Selection -->
                <div class="add-user-section">
                  <mat-form-field appearance="outline" class="user-select-field">
                    <mat-label>Add User</mat-label>
                    <mat-select (selectionChange)="addUserToFlag(flag, $event.value)" [value]="null">
                      <mat-option [value]="null" disabled>Select a user to add</mat-option>
                      @for (user of getAvailableUsersForFlag(flag); track user.UserId) {
                        <mat-option [value]="user.UserId">
                          {{ user.Name }} <span *ngIf="user.Email">({{ user.Email }})</span>
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>

                <!-- Clear All Button -->
                <div class="user-access-actions" *ngIf="hasUserAccessRestrictions(flag)">
                  <button mat-button color="warn" (click)="selectedUsersForFlag[flag.featureName] = []; saveUserAccess(flag)">
                    Clear All Users
                  </button>
                </div>
              </div>
            </div>

            <mat-divider *ngIf="!last"></mat-divider>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
</div>
